import React from "react";
import { trpc } from "@/utils/trpc";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	Video,
	Users,
	Calendar,
	Clock,
	Plus,
	PhoneCall,
	Mic,
	Monitor,
	Globe,
} from "lucide-react";

export interface MeetsOverviewProps {
	workspaceId: string;
	session?: any;
	currentWorkspace?: any;
}

export const MeetsOverview: React.FC<MeetsOverviewProps> = ({
	workspaceId,
	session,
	currentWorkspace,
}) => {
	const {
		isPending,
		error,
		data: channelData,
	} = useQuery(
		trpc.channel.getByWorkspace.queryOptions({
			workspaceId: Number(workspaceId),
		}),
	);

	if (isPending) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="text-center">
					<div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-primary border-b-2" />
					<p className="text-muted-foreground">Loading meetings...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="rounded-lg border border-border bg-destructive/10 p-8 text-center">
					<Video className="mx-auto mb-4 h-12 w-12 text-destructive" />
					<h3 className="mb-2 font-semibold text-destructive text-lg">
						Error loading meetings
					</h3>
					<p className="text-destructive/80">{error.message}</p>
				</div>
			</div>
		);
	}

	// Sample meeting data for demonstration
	const enhancedMeets = channelData?.map((channel: any) => {
		const meetingTypes = [
			"Team Standup",
			"Client Call",
			"Project Review",
			"Training Session",
			"1-on-1",
		];
		const statuses = ["scheduled", "live", "upcoming", "completed"];
		const status = statuses[Math.floor(Math.random() * statuses.length)];
		const startTime = new Date(
			Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000,
		);

		return {
			...channel,
			meetingType:
				meetingTypes[Math.floor(Math.random() * meetingTypes.length)],
			participants: Math.floor(Math.random() * 15) + 2,
			duration: [15, 30, 45, 60][Math.floor(Math.random() * 4)],
			startTime: startTime.toISOString(),
			status: status,
			isRecurring: Math.random() > 0.6,
			hasRecording: status === "completed" && Math.random() > 0.5,
			description: `${channel.name} team meeting`,
		};
	});

	const getStatusBadge = (status: string) => {
		const styles = {
			live: "bg-destructive text-destructive-foreground animate-pulse",
			upcoming: "bg-chart-1/20 text-chart-1",
			scheduled: "bg-muted/20 text-muted-foreground",
			completed: "bg-chart-2/20 text-chart-2",
		};
		return styles[status as keyof typeof styles] || styles.scheduled;
	};

	return (
		<div className="flex h-full flex-col bg-background">
			{/* Header */}
			<div className="border-border border-b bg-card px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="font-bold text-2xl text-foreground">Meetings</h1>
						<p className="mt-1 text-muted-foreground text-sm">
							Schedule and join video meetings
						</p>
					</div>
					<button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90">
						<Plus className="h-4 w-4" />
						New Meeting
					</button>
				</div>
			</div>

			{/* Scrollable Grid Container */}
			<div className="flex-1 overflow-y-auto p-6">
				{enhancedMeets && enhancedMeets.length > 0 ? (
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{enhancedMeets.map((meet: any) => (
							<Link
								key={meet.id}
								to="/$workspaceId/meets/$channelId"
								params={{ workspaceId: workspaceId, channelId: meet.id }}
								className="group"
							>
								<div className="flex h-full cursor-pointer flex-col rounded-lg border border-border bg-card p-5 text-card-foreground transition-all duration-200 hover:border-accent hover:shadow-lg">
									{/* Meeting Header */}
									<div className="mb-3 flex items-start justify-between">
										<div className="rounded-lg bg-accent p-2 transition-colors group-hover:bg-accent/80">
											<Video className="h-5 w-5 text-accent-foreground" />
										</div>
										<span
											className={`rounded-full px-2 py-1 font-medium text-xs ${getStatusBadge(meet.status)}`}
										>
											{meet.status === "live"
												? "LIVE"
												: meet.status.charAt(0).toUpperCase() +
													meet.status.slice(1)}
										</span>
									</div>

									{/* Meeting Info */}
									<h3 className="mb-1 font-semibold text-card-foreground transition-colors group-hover:text-primary">
										{meet.name}
									</h3>
									<span className="mb-2 text-muted-foreground text-xs">
										{meet.meetingType}
									</span>
									<p className="mb-4 line-clamp-2 flex-1 text-muted-foreground text-sm">
										{meet.description}
									</p>

									{/* Meeting Details */}
									<div className="mb-3 space-y-2">
										<div className="flex items-center gap-2 text-muted-foreground text-xs">
											<Calendar className="h-3 w-3" />
											<span>
												{new Date(meet.startTime).toLocaleDateString(
													undefined,
													{
														weekday: "short",
														month: "short",
														day: "numeric",
													},
												)}
											</span>
										</div>
										<div className="flex items-center gap-2 text-muted-foreground text-xs">
											<Clock className="h-3 w-3" />
											<span>
												{new Date(meet.startTime).toLocaleTimeString(
													undefined,
													{
														hour: "2-digit",
														minute: "2-digit",
													},
												)}{" "}
												• {meet.duration} min
											</span>
										</div>
									</div>

									{/* Meeting Stats */}
									<div className="flex items-center justify-between border-border border-t pt-3">
										<div className="flex items-center gap-3">
											<div className="flex items-center gap-1 text-muted-foreground text-xs">
												<Users className="h-3 w-3" />
												<span>{meet.participants}</span>
											</div>
											{meet.isRecurring && (
												<div className="flex items-center gap-1 text-muted-foreground text-xs">
													<Globe className="h-3 w-3" />
													<span>Recurring</span>
												</div>
											)}
										</div>
										{meet.status === "live" && (
											<button className="rounded-full bg-primary px-3 py-1 text-primary-foreground text-xs transition-colors hover:bg-primary/90">
												Join Now
											</button>
										)}
										{meet.hasRecording && (
											<span className="text-primary text-xs">📹 Recording</span>
										)}
									</div>
								</div>
							</Link>
						))}
					</div>
				) : (
					<div className="flex h-full flex-col items-center justify-center">
						<div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
							<Video className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
							<h3 className="mb-2 font-semibold text-foreground text-lg">
								No meetings scheduled
							</h3>
							<p className="mb-4 text-muted-foreground">
								Schedule your first meeting to connect with your team.
							</p>
							<button className="rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90">
								Schedule Meeting
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};